import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getApiErrorFromAxios, type ApiErrorBody } from "features/shared/errors/createApiError";
import { mapApiErrorToDomainError } from "features/shared/errors/mapToDomainError";

export type OnLogout = () => Promise<void>;
export type OnRefreshToken = () => Promise<boolean>;

let onLogout: OnLogout | null = null;
let onRefreshToken: OnRefreshToken | null = null;

export function setErrorInterceptorHandlers(logout: OnLogout, refreshToken: OnRefreshToken): void {
    onLogout = logout;
    onRefreshToken = refreshToken;
}

export async function handleResponseError(error: AxiosError): Promise<never> {
    const apiError = getApiErrorFromAxios(error);

    if (apiError?.body?.statusCode === 401 && onLogout && onRefreshToken) {
        const isLoggedIn = (await import("features/auth/store/authStore")).useAuthStore.getState().isLoggedIn;
        if (isLoggedIn) {
            const refreshed = await onRefreshToken();
            if (!refreshed) {
                await onLogout();
                const AuthErrorCodes = (await import("domain/auth/AuthErrors")).default;
                const DomainError = (await import("domain/error/DomainError")).default;
                throw new DomainError(AuthErrorCodes.SESSION_EXPIRED, "warning");
            }
            const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
            if (config && !config._retry) {
                config._retry = true;
                const api = (await import("features/shared/api/axiosInstance")).apiClient;
                return api.request(config) as Promise<never>;
            }
        }
    }

    if (apiError?.body) {
        throw mapApiErrorToDomainError(apiError.body as ApiErrorBody);
    }

    throw error;
}
